// Plan-gates a slice of UI: renders children when the plan has the feature,
// otherwise a locked card. The server enforces the same gate — this only
// saves the user a failed request.
//
// WHOSE plan matters depends on the slice: account features (creating a
// fleet) follow the viewer's own plan, but everything on a SITE follows the
// site OWNER's plan (the owner pays for the site; server
// `_require_site_feature`). Site tabs therefore pass `features` (the owner's,
// see lib/plan.siteFeatures) and `isOwner` — a member of someone else's site
// is told the owner's plan lacks it, not offered an upgrade of their own
// account that would change nothing.
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Lock } from "./icons";
import { useAuth } from "../auth/AuthContext";
import { asApiError, isUpgradeRequired } from "../api/client";
import type { PlanId } from "../api/types";
import { planName } from "../lib/plan";
import { ErrorBox } from "./ui";

export function requiredPlanFor(feature: string): PlanId {
  // Mirrors the backend catalog ordering: first plan that includes it.
  const map: Record<string, PlanId> = {
    monitor: "free", settings_backup: "free", device_health: "free",
    remote_admin: "basic", data_backup: "basic", alerts: "basic", export: "basic",
    share_site: "basic",
    entsoe_prices: "pro", optimization_insights: "pro", pv_forecast: "pro",
    planned_vs_actual: "pro", incident_reports: "pro", daily_analysis: "pro",
    monthly_savings: "pro", suggestions: "pro",
    fleet_dashboard: "fleet", roles: "fleet", remote_diagnostics: "fleet",
    fleet_reporting: "fleet", api_access: "fleet",
  };
  return map[feature] ?? "fleet";
}

/** The locked card. `isOwner` decides between "upgrade your plan" (with a
 *  CTA) and "the site owner's plan does not include this" (no CTA). */
export function LockedCard({ plan, isOwner = true }: { plan: string; isOwner?: boolean }) {
  const { t } = useTranslation();
  const name = planName(plan);
  return (
    <div className="card p-6 flex flex-col items-center text-center gap-3">
      <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-panel2 border border-border text-muted">
        <Lock size={18} />
      </span>
      <div className="text-sm text-muted">
        {isOwner
          ? t("gate.locked", { plan: name })
          : t("gate.ownerLocked", {
              plan: name,
              defaultValue: "This feature needs the site owner to be on the {{plan}} plan.",
            })}
      </div>
      {isOwner && (
        <Link to="/app/subscription" className="btn-primary">
          {t("gate.cta", { plan: name })}
        </Link>
      )}
    </div>
  );
}

export function UpgradeGate({
  feature,
  children,
  features,
  isOwner = true,
}: {
  feature: string;
  children: ReactNode;
  /** The plan features to check against. Omitted → the viewer's own plan
   *  (account features). `null` → unknown (an older server that does not
   *  report the owner's plan): render the children and let the server's
   *  answer decide — see QueryError. */
  features?: string[] | null;
  isOwner?: boolean;
}) {
  const { hasFeature } = useAuth();
  const has =
    features === undefined ? hasFeature(feature) : features === null ? true : features.includes(feature);
  if (has) return <>{children}</>;
  return <LockedCard plan={requiredPlanFor(feature)} isOwner={isOwner} />;
}

/** A failed query, rendered: a plan wall (upgrade_required 403) becomes the
 *  locked card, anything else an ErrorBox with the server's message — never
 *  a silent "no data". */
export function QueryError({ error, isOwner = true }: { error: unknown; isOwner?: boolean }) {
  if (isUpgradeRequired(error)) {
    const plan = error.requiredPlan || (error.feature ? requiredPlanFor(error.feature) : "");
    return <LockedCard plan={plan} isOwner={isOwner} />;
  }
  return <ErrorBox message={asApiError(error).message} />;
}
